// Verification state machine:
// idle → saving (POST/PUT in flight)
//     → success (POST/PUT 2xx, no verification needed)
//     → failed (POST/PUT 4xx/5xx + structured error body)
//     → verifying (timeout / 504 / network error from mutation)
//         → poll GET every VERIFY_POLL_INTERVAL_MS after VERIFY_INITIAL_DELAY_MS
//             → success (normalizeXSnapshot(response) deep-equals expectedSnapshot
//                        AND response.updatedAt > baseline)
//             → unknown (VERIFY_MAX_ATTEMPTS exhausted, no match found)
//             → failed (GET returns 403 or 404 — entity gone or access denied)

import { HTTP_STATUS_CODE_403, HTTP_STATUS_CODE_412 } from 'consts';
import { ADMIN_SAVE_PHASE_SET } from 'reducers/adminSavePhase';
import { startRequestTimer } from 'reducers/metrics';
import { call, delay, put, select } from 'redux-saga/effects';
import { startSubmit, stopSubmit } from 'redux-form';
import {
  VERIFY_INITIAL_DELAY_MS,
  VERIFY_MAX_ATTEMPTS,
  VERIFY_POLL_INTERVAL_MS,
} from 'consts/longRunningMutation';
import {
  SUCCESS,
  FAILURE,
  UNAUTH,
  createActionSuccess,
  ADD_MESSAGE,
  UNAUTHORIZED,
} from './actions';
import { getAuth } from './auth';
import { getError } from './saga';

const getDetails = (e) => {
  let { error: rawError, message, statusCode, response = {} } = e;

  if (e.response) {
    statusCode = e.response.status;
    message = e.response.statusText;
  }

  if (!e.response && e.data) {
    response = e.data;
    rawError = e.data.error;
  }

  return { message, rawError, response, statusCode };
};

const getVerifyUnknownMessage = (entityLabel) =>
  `We couldn't confirm whether your ${entityLabel} was saved. Please check the list before retrying.`;

export const isAmbiguousFailure = (error) => {
  if (!error) return false;
  if (error.code === 'ECONNABORTED') return true;
  if (/timeout.*exceeded/i.test(error.message ?? '')) return true;
  const status = error.status ?? error.statusCode ?? error.response?.status;
  if (status === 504) return true;
  return !error.response && error.code !== 'ERR_BAD_REQUEST';
};

export function* pollUntilVerified({
  verifyApi,
  getVerifyId,
  expectedSnapshot,
  baselineUpdatedAt,
  matchFn,
  auth,
  maxAttempts = VERIFY_MAX_ATTEMPTS,
}) {
  let attempts = 0;

  yield delay(VERIFY_INITIAL_DELAY_MS);

  while (attempts < maxAttempts) {
    try {
      const verifyId = yield call(getVerifyId);
      const response = yield call(verifyApi, verifyId, auth);

      if (matchFn(baselineUpdatedAt, expectedSnapshot, response)) {
        return { success: true, response };
      }

      attempts += 1;
      if (attempts < maxAttempts) {
        yield delay(VERIFY_POLL_INTERVAL_MS);
      }
    } catch (err) {
      const errStatus = err.status ?? err.statusCode ?? err.response?.status;
      if (errStatus === 403 || errStatus === 404) {
        return { success: false, error: err, isAuthError: true };
      }
      attempts += 1;
      if (attempts < maxAttempts) {
        yield delay(VERIFY_POLL_INTERVAL_MS);
      }
    }
  }

  return { success: false, isUnknown: true };
}

export const callActionWithSaveVerification = ({
  api,
  verifyApi,
  buildExpectedSnapshot,
  getBaselineUpdatedAt,
  matchFn,
  formatSuccessMessage,
  formatErrorMessage,
  prepareData,
  entityLabel,
  type,
}) => {
  return function* worker({ data = {}, form, meta }) {
    const timer = startRequestTimer(type.ACTION);
    const callData = prepareData ? prepareData(data) : data;
    const baselineUpdatedAt = getBaselineUpdatedAt(data);
    const expectedSnapshot = buildExpectedSnapshot(data);
    let result;

    if (form) {
      yield put(startSubmit(form));
    }

    const auth = yield select(getAuth);

    try {
      result = yield call(api, callData, auth);

      yield put(createActionSuccess(type)(result, callData, meta));

      if (formatSuccessMessage) {
        yield put({
          type: ADD_MESSAGE.ACTION,
          data: formatSuccessMessage(result, callData),
        });
      }

      if (data?.onSuccess) {
        try {
          data.onSuccess(result);
        } catch (callbackError) {
          // Isolate callback errors — do not let them corrupt the saga success path
        }
      }

      if (form) {
        yield put(stopSubmit(form, { _error: 'false' }));
      }

      yield put(timer.finish(SUCCESS, type));
    } catch (e) {
      const { message, statusCode, rawError, response } = getDetails(e);

      if (statusCode === HTTP_STATUS_CODE_403) {
        if (formatErrorMessage) {
          yield put({
            type: ADD_MESSAGE.ACTION,
            data: formatErrorMessage(e, callData),
          });
        }

        yield put({
          type: UNAUTHORIZED.ACTION,
          data: {
            authToken: {
              error: rawError,
              unauthorized: true,
            },
          },
        });
        yield put(timer.finish(UNAUTH));
        return result;
      }

      if (statusCode === HTTP_STATUS_CODE_412) {
        const error = getError(response, message, rawError);

        if (error) {
          if (formatErrorMessage) {
            yield put({
              type: ADD_MESSAGE.ACTION,
              data: formatErrorMessage(e, callData),
            });
          }

          yield put({ data, error, type: type.FAILURE });

          if (form) {
            yield put(stopSubmit(form, { _error: error.message || message }));
          }

          if (data?.onError) {
            data.onError(error);
          }

          yield put(timer.finish(FAILURE));
          return result;
        }
      }

      if (isAmbiguousFailure(e)) {
        yield put({
          type: ADMIN_SAVE_PHASE_SET,
          payload: { formName: form, phase: 'verifying' },
        });

        const pollResult = yield call(pollUntilVerified, {
          verifyApi,
          getVerifyId: () => data.id,
          expectedSnapshot,
          baselineUpdatedAt,
          matchFn,
          auth,
        });

        if (pollResult.success) {
          result = pollResult.response ?? { ...callData, id: callData.id };

          yield put(createActionSuccess(type)(result, callData, meta));

          if (formatSuccessMessage) {
            yield put({
              type: ADD_MESSAGE.ACTION,
              data: formatSuccessMessage(result, callData),
            });
          }

          if (form) {
            yield put(stopSubmit(form, { _error: 'false' }));
          }

          yield put(timer.finish(SUCCESS, type));
        } else if (pollResult.isAuthError) {
          yield put(
            stopSubmit(form, {
              _error: 'Unauthorized. Your save may have completed.',
            })
          );
          yield put({ data, error: pollResult.error, type: type.FAILURE });
          yield put(timer.finish(FAILURE));
        } else if (pollResult.isUnknown) {
          yield put({
            type: ADMIN_SAVE_PHASE_SET,
            payload: { formName: form, phase: 'unknown' },
          });

          yield put(
            stopSubmit(form, {
              _error: getVerifyUnknownMessage(entityLabel),
            })
          );
          yield put({
            data,
            error: {
              code: 'VERIFY_UNKNOWN',
              message: 'Verification budget exhausted',
            },
            type: type.FAILURE,
          });
          yield put(timer.finish(FAILURE));
        }

        yield put({
          type: ADMIN_SAVE_PHASE_SET,
          payload: { formName: form, phase: null },
        });
        return result;
      }

      if (formatErrorMessage) {
        yield put({
          type: ADD_MESSAGE.ACTION,
          data: formatErrorMessage(e, callData),
        });
      }

      const error = getError(response, message, rawError);

      if (error) {
        yield put({ data, error, type: type.FAILURE });

        if (form) {
          yield put(stopSubmit(form, { _error: error.message || message }));
        }

        if (data?.onError) {
          data.onError(error);
        }

        yield put(timer.finish(FAILURE));
        return result;
      }

      yield put(createActionSuccess(type)([], callData, meta));
      yield put(timer.finish(SUCCESS));
    }

    return result;
  };
};
